// Header com informações do usuário Firebase e conexão com carteiras

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, Wallet, ChevronDown, Zap, ArrowLeft, Settings, AlertCircle, Shield, BookOpen, BadgeInfo, } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { usePendingFees } from '@/contexts/PendingFeeContext';
import { useToast } from '@/hooks/useToast';
import WalletConnectionModal from '@/components/wallets/WalletConnectionModal';
import { formatCurrencyDisplay } from '@/utils/currency';

// Logo da aplicação
import logoSvg from '@/assets/images/nexusprime.svg';

// Ícones de stablecoins
import usdcIcon from '@/assets/images/usdc.svg';
import usdtIcon from '@/assets/images/usdt.svg';

// Mapeamento de símbolo para ícone
const TOKEN_ICONS = {
  'USDC': usdcIcon,
  'USDT': usdtIcon,
};

export default function Header({ balance = 0, onOpenAboutPage, onBackToTransfer, onOpenSettings, onOpenOverview, onOpenQuitarTaxas, onOpenAdminManagement, showingCarteiras, showingSettings, showingAdminManagement }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isCurrentUserAdmin } = useAdmin();
  const { totalPendingUSD, hasPendingFees } = usePendingFees();
  const {
    connectedWallet,
    walletAddress,
    walletStablecoinBalances,
    walletStablecoinTotal,
    walletStablecoinChainId,
    selectedNetwork,
    supportedNetworks,
    getChainDisplayName,
    disconnectWallet,
    switchNetwork,
    isPhantomEVM,
  } = useWalletConnection();

  const showEVMWalletUI =
    connectedWallet === 'metamask' ||
    connectedWallet === 'walletconnect' ||
    (connectedWallet === 'phantom' && isPhantomEVM);
  const { success, error: showError } = useToast();
  const [showMenu, setShowMenu] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showStablecoinDropdown, setShowStablecoinDropdown] = useState(false);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);
  const stablecoinDropdownRef = useRef(null);
  const networkDropdownRef = useRef(null);
  const menuRef = useRef(null);

  // Saldo: stablecoins (MetaMask / WalletConnect / Phantom EVM) ou 0 quando desconectado
  const displayBalance =
    showEVMWalletUI && walletStablecoinTotal
      ? parseFloat(walletStablecoinTotal)
      : connectedWallet === 'phantom'
        ? 0
        : balance ?? 0;

  const stablecoinEntries = Object.entries(walletStablecoinBalances || {});
  const hasStablecoins = stablecoinEntries.length > 0;

  useEffect(() => {
    function handleClickOutside(event) {
      if (stablecoinDropdownRef.current && !stablecoinDropdownRef.current.contains(event.target)) {
        setShowStablecoinDropdown(false);
      }
      if (networkDropdownRef.current && !networkDropdownRef.current.contains(event.target)) {
        setShowNetworkDropdown(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNetworkChange = (network) => {
    switchNetwork(network);
    setShowNetworkDropdown(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      success(t('header.logoutSuccess'));
    } catch (err) {
      showError(t('header.logoutError'));
    }
  };

  const handleDisconnectWallet = async () => {
    try {
      await disconnectWallet();
      setShowMenu(false);
    } catch (err) {
      showError(t('header.disconnectWalletError'));
    }
  };

  const getWalletDisplayName = () => {
    if (!walletAddress) return '';
    return `${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`;
  };

  return (
    <>
      <header className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 border-b border-purple-500 border-opacity-20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between min-h-[56px]">
          <div className="flex items-center">
            <img src={logoSvg} alt="Logo" className="h-8 w-auto mr-2" />
            <h1 className="hidden sm:block text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400" title="Nexusprime">
              Nexusprime
            </h1>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            {/* Saldo em Stablecoins (só com carteira); sem carteira mostra o botão Conectar Carteira — mutuamente exclusivos */}
            {showEVMWalletUI && (
              <div className="text-right relative" ref={stablecoinDropdownRef}>
                <p className="text-gray-400 text-sm">{t('header.balanceStablecoins')}</p>
                <div className="flex items-center gap-2 justify-end">
                  <div className="relative inline-flex">
                    <button
                      type="button"
                      onClick={() => setShowStablecoinDropdown(!showStablecoinDropdown)}
                      className="p-1 rounded hover:bg-white/10 transition text-gray-400 hover:text-white"
                      aria-expanded={showStablecoinDropdown}
                      aria-haspopup="true"
                    >
                      <ChevronDown
                        size={18}
                        className={`transition-transform ${showStablecoinDropdown ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {showStablecoinDropdown && (
                      <div className="absolute left-0 top-full mt-1 w-48 py-2 bg-gray-800 border border-purple-500/30 rounded-lg shadow-xl z-50">
                        {hasPendingFees && totalPendingUSD > 0 && onOpenQuitarTaxas && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowStablecoinDropdown(false);
                              onOpenQuitarTaxas();
                            }}
                            className="w-full text-left px-3 py-2 text-amber-400 hover:bg-amber-900/20 transition flex items-center gap-2 border-b border-gray-700/50 mb-2"
                          >
                            <AlertCircle size={16} />
                            {t('header.quitarTaxas')}: {formatCurrencyDisplay(totalPendingUSD, 'USD')}
                          </button>
                        )}
                        <p className="px-3 py-1 text-xs text-gray-500 font-medium border-b border-gray-700/50 mb-2">
                          {t('header.detalhePorToken')}
                        </p>
                        {hasStablecoins ? (
                          stablecoinEntries.map(([symbol, amount]) => (
                            <div
                              key={symbol}
                              className="flex justify-between items-center px-3 py-1.5 text-sm text-white hover:bg-gray-700/50"
                            >
                              <div className="flex items-center gap-2">
                                {TOKEN_ICONS[symbol] ? (
                                  <img src={TOKEN_ICONS[symbol]} alt={symbol} className="w-5 h-5" />
                                ) : (
                                  <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center text-xs">
                                    {symbol.charAt(0)}
                                  </div>
                                )}
                                <span className="font-medium">{symbol}</span>
                              </div>
                              <span className="text-green-400">{formatCurrencyDisplay(Number(amount), 'USD')}</span>
                            </div>
                          ))
                        ) : (
                          <p className="px-3 py-2 text-sm text-gray-400">{t('header.noStablecoinFound')}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {hasPendingFees && totalPendingUSD > 0 && (
                    <AlertCircle size={20} className="text-amber-400 shrink-0" title={`${t('header.pendingFeesTitle')}: ${formatCurrencyDisplay(totalPendingUSD, 'USD')}`} />
                  )}
                  <p className="text-2xl font-bold text-white">
                    {formatCurrencyDisplay(displayBalance ?? 0, 'USD')}
                  </p>
                </div>
              </div>
            )}

            {/* Botão Conectar carteira (visível apenas sem carteira EVM) */}
            {!showEVMWalletUI && (
              <button
                type="button"
                onClick={() => setShowWalletModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600/80 hover:bg-purple-600 border border-purple-500/50 rounded-lg text-white text-sm font-medium transition"
              >
                <Wallet size={18} />
                <span className="hidden sm:inline">{t('header.connectWallet')}</span>
              </button>
            )}

            {/* Seletor de Rede (sempre visível); com carteira mostra também o nome encurtado */}
            <div className="relative inline-flex" ref={networkDropdownRef}>
              <button
                type="button"
                onClick={() => setShowNetworkDropdown(!showNetworkDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800/60 border border-purple-500/30 rounded-lg hover:bg-gray-700/60 transition"
              >
                <ChevronDown
                  size={14}
                  className={`text-gray-400 shrink-0 transition-transform ${showNetworkDropdown ? 'rotate-180' : ''}`}
                />
                {selectedNetwork && (
                  <img
                    src={selectedNetwork.icon}
                    alt={selectedNetwork.name}
                    className="w-5 h-5 shrink-0"
                  />
                )}
                {/* Se tiver carteira EVM conectada, mostra o nome da rede. Se não tiver carteira, mostra "Selecione a Rede" */}
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-xs font-semibold text-white truncate hidden sm:block">
                    {selectedNetwork?.name || t('header.network')}
                  </span>
                  <span className="text-[10px] text-gray-400 hidden sm:block truncate hidden sm:block">
                    {showEVMWalletUI ? getWalletDisplayName() : t('header.connectWalletShort')}
                  </span>
                </div>
              </button>

              {showNetworkDropdown && (
                <div className="absolute left-0 top-full mt-1 w-44 py-2 bg-gray-800 border border-purple-500/30 rounded-lg shadow-xl z-50">
                  <p className="px-3 py-1 text-xs text-gray-500 font-medium border-b border-gray-700/50 mb-1">
                    {t('header.networkDropdownSelect')}
                  </p>
                  {supportedNetworks?.map((network) => (
                    <button
                      key={network.chainId}
                      type="button"
                      onClick={() => handleNetworkChange(network)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-purple-900/30 transition ${
                        selectedNetwork?.chainId === network.chainId ? 'bg-purple-900/20' : ''
                      }`}
                    >
                      <img src={network.icon} alt={network.name} className="w-4 h-4" />
                      <span>{network.name}</span>
                      {selectedNetwork?.chainId === network.chainId && (
                        <span className="ml-auto text-green-400 text-xs">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Menu de Usuário (apenas foto) */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center hover:bg-purple-900/30 p-1.5 rounded-full transition"
                aria-label={t('header.openUserMenu')}
              >
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
                  </div>
                )}
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-purple-500/30 rounded-lg shadow-lg z-50">
                  <div className="p-3 border-b border-purple-500/20 flex justify-center">
                    {user?.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt=""
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-purple-600 flex items-center justify-center text-white text-xl font-medium">
                        {user?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>

                  {/* Opção: Visão geral */}
                  {onOpenOverview && (
                    <button
                      onClick={() => {
                        onOpenOverview();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-purple-300 hover:bg-purple-900/20 transition flex items-center gap-2 border-b border-purple-500/20"
                    >
                      <BookOpen size={16} />
                      {t('header.overview')}
                    </button>
                  )}
                  {/* Opção: Configurações */}
                  <button
                    onClick={() => {
                      onOpenSettings?.();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-purple-300 hover:bg-purple-900/20 transition flex items-center gap-2 border-b border-purple-500/20"
                  >
                    <Settings size={16} />
                    {t('header.settings')}
                  </button>

                  {/* Opção: Gerenciar Admins (só para admins) */}
                  {isCurrentUserAdmin && onOpenAdminManagement && (
                    <button
                      onClick={() => {
                        onOpenAdminManagement();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-yellow-300 hover:bg-yellow-900/20 transition flex items-center gap-2 border-b border-purple-500/20"
                    >
                      <Shield size={16} />
                      {t('admin.title')}
                    </button>
                  )}

                  {/* Opção: Conectar Carteira */}
                  <button
                    onClick={() => {
                      setShowWalletModal(true);
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-blue-400 hover:bg-blue-900/20 transition flex items-center gap-2 border-b border-purple-500/20"
                  >
                    <Zap size={16} />
                    {connectedWallet ? t('header.changeWallet') : t('header.connectWallet')}
                  </button>

                  {/* Opção: Desconectar Carteira (se conectado) */}
                  {connectedWallet && (
                    <button
                      onClick={() => {
                        handleDisconnectWallet();
                      }}
                      className="w-full text-left px-4 py-2 text-yellow-400 hover:bg-yellow-900/20 transition flex items-center gap-2 border-b border-purple-500/20"
                    >
                      <Wallet size={16} />
                      {t('header.disconnectWallet')}
                    </button>
                  )}

                  {/* About */}
                  <button
                    onClick={() => {
                      onOpenAboutPage?.();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-purple-300 hover:bg-purple-900/20 transition flex items-center gap-2 border-b border-purple-500/20"
                  >
                    <BadgeInfo size={16} />
                    {t('header.aboutPage')}
                  </button>

                  {/* Opção: Sair */}
                  <button
                    onClick={() => {
                      handleLogout();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-red-400 hover:bg-red-900/20 transition flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Modal de Conexão de Carteira */}
      <WalletConnectionModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
      />
    </>
  );
}
